import React, { useState, useMemo } from "react";
import { api, formatBRL } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2, Percent } from "lucide-react";

export default function ChangeInterestModal({ contract, onClose, onDone }) {
  const cc = contract?.current_cycle || {};
  const capital = Number(cc.capital ?? contract?.capital ?? 0);
  const currentPct = Number(cc.interest_percent ?? contract?.interest_percent ?? 0);
  const totalPaid = Number(cc.total_paid ?? 0);

  const [newPct, setNewPct] = useState(String(currentPct ?? ""));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const preview = useMemo(() => {
    const pct = parseFloat(String(newPct).replace(",", "."));
    if (!Number.isFinite(pct) || pct < 0) return null;
    const interest = Math.round(capital * (pct / 100) * 100) / 100;
    const totalDue = Math.round((capital + interest) * 100) / 100;
    const balance = Math.round((totalDue - totalPaid) * 100) / 100;
    return { pct, interest, totalDue, balance };
  }, [newPct, capital, totalPaid]);

  const submit = async (e) => {
    e.preventDefault();
    if (!preview) { toast.error("% inválido"); return; }
    if (reason.trim().length < 5) { toast.error("Motivo precisa ter ao menos 5 caracteres"); return; }
    setSaving(true);
    try {
      await api.post(`/contracts/${contract.id}/change-interest`, {
        new_interest_percent: preview.pct,
        reason: reason.trim(),
      });
      toast.success(`% atualizado: ${currentPct}% → ${preview.pct}%`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao alterar % de juros");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="change-interest-modal">
      <div className="fc-card w-full max-w-lg my-8 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <Percent size={16} className="text-[#D4AF37]"/> Alterar % de juros
            </h3>
            <p className="text-[12px] text-[#A3A3A3] mt-1">
              Recalcula juros, total devido e saldo do <strong>ciclo atual</strong>. Pagamentos
              já feitos permanecem.
            </p>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-white" data-testid="change-interest-close">
            <X size={16}/>
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-[#737373]">Capital</div>
              <div className="font-mono-tab text-sm mt-0.5">{formatBRL(capital)}</div>
            </div>
            <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-[#737373]">% atual</div>
              <div className="font-mono-tab text-sm mt-0.5 text-[#A3A3A3]" data-testid="current-interest-pct">{currentPct}%</div>
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">Novo % de juros</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="1000"
              required
              className="fc-input w-full text-[13px]"
              value={newPct}
              onChange={(e) => setNewPct(e.target.value)}
              placeholder="Ex.: 40"
              data-testid="new-interest-pct-input"
            />
          </div>

          {preview && (
            <div className="rounded-md border border-[#D4AF37]/30 bg-[#0f0f0f] p-3" data-testid="change-interest-preview">
              <div className="text-[10px] uppercase tracking-wider text-[#D4AF37] mb-2">Prévia após salvar</div>
              <div className="grid grid-cols-3 gap-2 text-[12px]">
                <div>
                  <div className="text-[10px] text-[#737373]">Juros</div>
                  <div className="font-mono-tab text-[#D4AF37]" data-testid="preview-interest">{formatBRL(preview.interest)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-[#737373]">Total devido</div>
                  <div className="font-mono-tab" data-testid="preview-total-due">{formatBRL(preview.totalDue)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-[#737373]">Saldo restante</div>
                  <div className={`font-mono-tab ${preview.balance > 0 ? "text-yellow-300" : "text-emerald-300"}`} data-testid="preview-balance">{formatBRL(preview.balance)}</div>
                </div>
              </div>
              {totalPaid > 0 && (
                <div className="text-[10.5px] text-[#737373] mt-2">
                  Já pago no ciclo: <span className="font-mono-tab">{formatBRL(totalPaid)}</span> (mantido)
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">Motivo (obrigatório, mín. 5 caracteres)</label>
            <textarea
              className="fc-input w-full text-[13px]"
              rows={3}
              required
              minLength={5}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: ajuste por renegociação verbal aprovada pelo cliente"
              data-testid="change-interest-reason"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="fc-btn-ghost text-xs" data-testid="change-interest-cancel">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="fc-btn-gold flex items-center gap-1.5 text-xs"
              data-testid="change-interest-submit"
            >
              {saving ? <Loader2 size={12} className="animate-spin"/> : <Percent size={12}/>}
              Confirmar alteração
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
